import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, KeyRound, CheckCircle2, Loader2 } from "lucide-react";
import { BrandLogo } from "@/projects/components/brand-logo";
import { Button } from "@/projects/components/ui/button";
import { Card } from "@/projects/components/ui/card";
import {
  PasswordFields,
  canSubmitPasswords,
} from "@/projects/components/password-field";
import { axiosConfig } from "@/services/axiosConfig";
import { toast } from "@/projects/lib/toast";

export const Route = createFileRoute("/forgot-password-reset/$id")({
  head: () => ({
    meta: [
      { title: "Reset Password — રાજનગર જ્ઞાનોત્સવ 2.0" },
      {
        name: "description",
        content: "Set a new password for your Rajnagar Gyanotsav 2.0 account.",
      },
    ],
  }),
  component: ForgotPasswordResetPage,
});

function getResetErrorMessage(error: unknown): string {
  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = err?.response?.data;
  if (data) {
    for (const key of [
      "message",
      "detail",
      "error",
      "new_password",
      "confirm_password",
      "non_field_errors",
    ]) {
      const v = data[key];
      if (typeof v === "string" && v.trim()) {
        return v;
      }
      if (Array.isArray(v) && typeof v[0] === "string") {
        return v[0];
      }
    }
  }
  return err?.message || "Failed to reset password. Please try again.";
}

function ForgotPasswordResetPage() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setShowFieldErrors(true);

    const check = canSubmitPasswords(newPassword, confirmPassword);
    if (!check.ok) {
      setError(check.message);
      return;
    }
    if (!id) {
      setError("Invalid or missing reset link. Please request a new one.");
      return;
    }

    setLoading(true);
    try {
      // POST /forgot-password-reset/{token}/  { new_password, confirm_password }
      await axiosConfig.post(
        `/forgot-password-reset/${id}/`,
        {
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      setDone(true);
      toast.success("Password reset successfully.");
    } catch (err) {
      const message = getResetErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-background relative flex min-h-screen items-center justify-center overflow-hidden p-4">
      <div className="gradient-hero absolute inset-0 opacity-95" />
      <div className="motif-bg absolute inset-0 opacity-50" />

      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="mb-4 inline-flex items-center gap-2 text-sm text-white/90 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <Card className="shadow-card p-8">
          <div className="mb-6 flex items-center gap-3">
            <BrandLogo className="h-11 w-11" />
            <div>
              <div className="text-guj text-sm font-bold">
                રાજનગર જ્ઞાનોત્સવ 2.0
              </div>
              <div className="text-muted-foreground text-[10px] font-medium tracking-widest uppercase">
                Account Recovery
              </div>
            </div>
          </div>

          {!done ? (
            <>
              <div className="border-saffron/40 bg-saffron-soft text-saffron inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium">
                <KeyRound className="h-3.5 w-3.5" /> Reset Password
              </div>
              <h1 className="mt-3 text-2xl font-bold">Set a new password</h1>
              <p className="text-muted-foreground mt-1 text-sm">
                Enter a new password for your account, then confirm it below.
              </p>

              <form
                onSubmit={e => void handleSubmit(e)}
                className="mt-6 space-y-4"
              >
                <PasswordFields
                  password={newPassword}
                  confirmPassword={confirmPassword}
                  onPasswordChange={setNewPassword}
                  onConfirmChange={setConfirmPassword}
                  disabled={loading}
                  showErrors={showFieldErrors}
                />

                {error && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-600">
                    {error}
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    "Save new password"
                  )}
                </Button>
              </form>

              <div className="text-muted-foreground mt-6 flex items-center justify-between text-xs">
                <Link to="/login/teacher" className="hover:text-foreground">
                  ← Teacher login
                </Link>
                <Link to="/login/admin" className="hover:text-foreground">
                  Admin login →
                </Link>
              </div>
            </>
          ) : (
            <div className="py-6 text-center">
              <div className="bg-success/10 text-success mx-auto grid h-14 w-14 place-items-center rounded-full">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h1 className="mt-4 text-xl font-bold">Password updated</h1>
              <p className="text-muted-foreground mt-2 text-sm">
                Your password has been reset successfully. You can now sign in
                with your new password.
              </p>
              <div className="mt-6 flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate({ to: "/login/teacher" })}
                >
                  Teacher login
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => navigate({ to: "/login/admin" })}
                >
                  Admin login
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
